import { useState, useEffect, useCallback } from 'react';
import { workshopServices } from '@/mocks/workshopServices';
import HorizontalScroll from '@/components/feature/HorizontalScroll';

interface ServiceItem {
  id: string;
  icon: string;
  title: string;
  detail: string;
  images: string[];
}

export default function WorkshopServices() {
  const [hovered, setHovered] = useState<string | null>(null);
  const [selected, setSelected] = useState<ServiceItem | null>(null);
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  const currentImages = selected?.images ?? [];
  const totalImages = currentImages.length;

  const goNext = useCallback(() => {
    if (expandedIndex === null) return;
    setExpandedIndex((expandedIndex + 1) % totalImages);
  }, [expandedIndex, totalImages]);

  const goPrev = useCallback(() => {
    if (expandedIndex === null) return;
    setExpandedIndex((expandedIndex - 1 + totalImages) % totalImages);
  }, [expandedIndex, totalImages]);

  useEffect(() => {
    if (expandedIndex === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') goNext();
      else if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === 'Escape') setExpandedIndex(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [expandedIndex, goNext, goPrev]);

  return (
    <section id="servicios" className="py-16 md:py-20 bg-background-100">
      <div className="max-w-[1280px] mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-10 md:mb-14">
          <h2 className="font-heading text-4xl md:text-5xl text-foreground-50 tracking-wide">
            Nuestros Servicios
          </h2>
          <p className="text-foreground-300 text-sm md:text-base max-w-2xl mx-auto mt-4 leading-relaxed">
            Servicios especializados de transformación del acero con precisión y calidad
            garantizada.
          </p>
        </div>

        {/* Horizontal scrollable carousel */}
        <HorizontalScroll gap={16}>
          {workshopServices.map((svc, index) => (
            <div
              key={svc.id}
              className={`group relative flex-shrink-0 w-[260px] md:w-[280px] bg-background-50 border border-background-300/40 rounded-sm p-6 cursor-pointer transition-all duration-300 overflow-hidden ${
                hovered === svc.id ? 'border-primary-500/60 shadow-sm' : ''
              }`}
              onMouseEnter={() => setHovered(svc.id)}
              onMouseLeave={() => setHovered(null)}
              onClick={() => setSelected(svc)}
            >
              {/* Sutil número decorativo arriba a la derecha */}
              <span className="absolute top-4 right-4 font-heading text-3xl text-foreground-700/20 leading-none select-none">
                {String(index + 1).padStart(2, '0')}
              </span>

              {/* Barra lateral sutil que crece en hover */}
              <div className={`absolute left-0 top-0 bottom-0 w-[3px] bg-primary-500 transition-all duration-300 ${
                hovered === svc.id ? 'opacity-100' : 'opacity-0'
              }`} />

              <div className="relative z-10">
                <h3 className="font-heading text-lg text-foreground-50 tracking-wide mb-2 pr-10">
                  {svc.title}
                </h3>
                {svc.detail && (
                  <p className="text-foreground-300 text-sm leading-relaxed">
                    {svc.detail}
                  </p>
                )}

                {/* Indicador "Ver fotos" en hover */}
                <div className={`mt-4 flex items-center gap-1.5 text-primary-500 text-xs font-medium transition-all duration-300 ${
                  hovered === svc.id ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
                }`}>
                  <span>Ver fotos</span>
                  <i className="ri-arrow-right-line text-sm"></i>
                </div>
              </div>
            </div>
          ))}
        </HorizontalScroll>
      </div>

      {/* Modal overlay */}
      {selected && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm"
          onClick={() => setSelected(null)}
        >
          <div
            className="bg-background-50 border border-background-300/40 rounded-sm w-full max-w-[900px] max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal header */}
            <div className="flex items-center justify-between p-5 md:p-6 border-b border-background-300/40">
              <div>
                <h3 className="font-heading text-xl text-foreground-50 tracking-wide">
                  {selected.title}
                </h3>
                {selected.detail && (
                  <p className="text-foreground-300 text-sm mt-0.5">
                    {selected.detail}
                  </p>
                )}
              </div>
              <button
                onClick={() => setSelected(null)}
                className="w-9 h-9 flex items-center justify-center rounded-sm text-foreground-300 hover:text-foreground-50 hover:bg-background-200 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            {/* Modal body - images */}
            <div className="p-5 md:p-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {selected.images.map((img, idx) => (
                  <div
                    key={idx}
                    className="relative overflow-hidden rounded-sm border border-background-300/40 bg-background-200 aspect-[4/3] cursor-pointer group/img"
                    onClick={() => setExpandedIndex(idx)}
                  >
                    <img
                      src={img}
                      alt={`${selected.title} - imagen ${idx + 1}`}
                      className="w-full h-full object-cover"
                      loading={idx === 0 ? undefined : 'lazy'}
                      onLoad={(e) => {
                        const el = e.currentTarget as HTMLImageElement;
                        el.style.opacity = '1';
                      }}
                      onError={(e) => {
                        const el = e.currentTarget as HTMLImageElement;
                        el.style.display = 'none';
                        const parent = el.parentElement;
                        if (parent) {
                          const fallback = document.createElement('div');
                          fallback.className = 'absolute inset-0 flex items-center justify-center text-foreground-400 text-sm';
                          fallback.textContent = 'No se pudo cargar la imagen';
                          parent.appendChild(fallback);
                        }
                      }}
                      style={{ opacity: 0, transition: 'opacity 0.4s ease' }}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox overlay con flechas de navegación */}
      {expandedIndex !== null && selected && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 p-4"
          onClick={() => setExpandedIndex(null)}
        >
          <div className="relative w-full max-w-[1200px] flex flex-col items-center" onClick={(e) => e.stopPropagation()}>

            {/* Botón cerrar */}
            <button
              onClick={() => setExpandedIndex(null)}
              className="absolute -top-12 right-0 w-10 h-10 flex items-center justify-center rounded-sm text-foreground-50 hover:text-white hover:bg-background-200/30 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>

            {/* Contador */}
            {totalImages > 1 && (
              <div className="absolute -top-12 left-0 text-white/60 text-sm font-medium">
                {expandedIndex + 1} / {totalImages}
              </div>
            )}

            {/* Flecha izquierda */}
            {totalImages > 1 && (
              <button
                onClick={(e) => { e.stopPropagation(); goPrev(); }}
                className="absolute left-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/25 text-white transition-colors cursor-pointer backdrop-blur-sm"
                aria-label="Imagen anterior"
              >
                <i className="ri-arrow-left-s-line text-2xl"></i>
              </button>
            )}

            {/* Flecha derecha */}
            {totalImages > 1 && (
              <button
                onClick={(e) => { e.stopPropagation(); goNext(); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/25 text-white transition-colors cursor-pointer backdrop-blur-sm"
                aria-label="Imagen siguiente"
              >
                <i className="ri-arrow-right-s-line text-2xl"></i>
              </button>
            )}

            {/* Imagen */}
            <img
              src={currentImages[expandedIndex]}
              alt={`${selected.title} - imagen ${expandedIndex + 1}`}
              className="w-full max-h-[85vh] object-contain rounded-sm"
            />

            {/* Dots indicadores (solo si hay más de 1) */}
            {totalImages > 1 && (
              <div className="flex items-center gap-2 mt-4">
                {currentImages.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={(e) => { e.stopPropagation(); setExpandedIndex(idx); }}
                    className={`w-2 h-2 rounded-full transition-all cursor-pointer ${
                      idx === expandedIndex
                        ? 'bg-white w-6'
                        : 'bg-white/40 hover:bg-white/70'
                    }`}
                    aria-label={`Ir a imagen ${idx + 1}`}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}